import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import pl from "shared/messages/pl";
import { Loader } from "@fluentui/react-northstar";
import styles from "./dropUpload.module.scss";
import { ResponsiveDialog } from "ecom-teams-apps-core";
import { constants } from "shared/constants";
import { calculateFileSize } from "../../utils/fileUtils";

interface DropUploadProps {
    fileSizeLimit: number;

    onUpload: (files: File[]) => Promise<void>;
}

const DropUpload: React.FC<DropUploadProps> = (props: DropUploadProps) => {
    const [isActive, setIsActive] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isValidationErrorDialogVisible, setIsValidationErrorDialogVisible] = useState(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState("");

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        setIsActive(false);

        setIsBusy(true);
        await props.onUpload(acceptedFiles);
        setIsBusy(false);
    }, []);

    const fileSizeValidator = (file: File) => {
        if (file.size > props.fileSizeLimit) {
            return {
                code: constants.filesValidationCodes.fileTooLarge,
                message: constants.filesValidationCodes.fileTooLarge,
            };
        }

        return null;
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        multiple: false,
        onDragEnter: () => {
            setIsActive(true);
        },
        onDragLeave: () => {
            setIsActive(false);
        },
        onDrop: onDrop,
        onDropRejected: (exceptions) => {
            switch (exceptions[0].errors[0].code) {
                case constants.filesValidationCodes.fileTooLarge:
                    setValidationErrorMessage(
                        pl.taskDetails.attachments.validationError.contents.fileTooLarge(
                            calculateFileSize(props.fileSizeLimit)
                        )
                    );
                    break;
                case constants.filesValidationCodes.tooManyFiles:
                    setValidationErrorMessage(pl.taskDetails.attachments.validationError.contents.tooManyFiles);
                    break;
                default:
                    setValidationErrorMessage(pl.taskDetails.attachments.validationError.contents.uploadFailed);
                    break;
            }
            setIsValidationErrorDialogVisible(true);
        },
        disabled: isBusy,
        validator: fileSizeValidator,
    });

    return (
        <>
            <div {...getRootProps({ className: isActive ? styles.dropzoneActive : styles.dropzoneInactive })}>
                <input {...getInputProps()} />
                {isBusy ? (
                    <Loader
                        size="small"
                        title={pl.taskDetails.attachments.loading.add}
                        label={pl.taskDetails.attachments.loading.add}
                    />
                ) : (
                    <div>
                        {pl.taskDetails.attachments.dropzone.dropToUpload}{" "}
                        <button type="button" onClick={open} className={styles.browse}>
                            {pl.taskDetails.attachments.dropzone.browse}
                        </button>
                    </div>
                )}
            </div>
            <ResponsiveDialog
                confirmButton={pl.common.close}
                onConfirm={() => {
                    setIsValidationErrorDialogVisible(false);
                }}
                open={isValidationErrorDialogVisible}
                header={pl.taskDetails.attachments.validationError.header}
                content={validationErrorMessage}
            />
        </>
    );
};

export default DropUpload;
